// ============================================
// Live Data Source — ChatDataSource (mode: 'live')
//
// Wires real services: Gemini NLP extraction,
// Lab Analyst vision stream, real protocol pipeline
// (pre-filter → Phase1 → Phase2 → gate classification),
// and Gemini multi-turn chat.
// ============================================

import type { BiomarkerResult } from '../../types';
import type { ChatDataSource } from '../../types/analystBar';
import type { LabValue, Demographics, ModifierSet } from '../../types/patientJourney';
import { extractSignals, buildCaptureReinforcement } from '../fashionPlate/captureExtractor';
import { DEFAULT_CAPTURE_CONFIG } from '../../types/fashionPlate';
import { streamLabAnalysis } from '../agents/labAnalystStream';
import { runPhase1, runPhase2 } from '../patientJourney/analystEngine';
import { ModifierCollector } from '../patientJourney/modifierCollector';
import { geminiChat } from '../ai/providers/gemini';
import { loadBrandDisplay } from '../firestore/brandDisplayService';
import { runPreFilter } from '../protocol/preFilterEngine';
import { classifyGate } from '../protocol/gateClassifier';
import type { NurseOrchestrator } from '../nurse/NurseOrchestrator';
import { SEED_SUPPLEMENTS } from '../../data/formulary/seedSupplements';
import { SEED_RX } from '../../data/formulary/seedRx';
import { SEED_LONGEVITY } from '../../data/formulary/seedLongevity';
import { SEED_SAFETY_RULES } from '../../data/formulary/seedSafetyRules';
import { CRITICAL_SAFETY_RULES } from '../../data/criticalSafetyRules';
import { TIER1_SUPPLEMENT_SAFETY_RULES } from '../../data/formulary/tier1SupplementSafetyRules';
import { TIER2_RX_SAFETY_RULES } from '../../data/formulary/tier2RxSafetyRules';
import { TIER3_LONGEVITY_SAFETY_RULES } from '../../data/formulary/tier3LongevitySafetyRules';
import type { PreFilterInput } from '../../types/preFilter';

// ── HRT Defaults Constant (E2) ──
// Core HRT products injected for postmenopausal/surgical menopause patients.
// Extracted from inline list to enable cross-referencing with safety rules.
const CORE_HRT_DEFAULTS: Array<{
  id: string;
  reason: string;
  checkBiomarker?: string;
  adequateThreshold?: number;
}> = [
  { id: 'rx-estradiol-patch', reason: 'Postmenopausal estrogen replacement', checkBiomarker: 'estradiol', adequateThreshold: 50 },
  { id: 'rx-progesterone-prometrium', reason: 'Progesterone for sleep + endometrial protection', checkBiomarker: 'progesterone', adequateThreshold: 1 },
  { id: 'rx-testosterone-cream-female', reason: 'Low testosterone — libido + cognition + energy', checkBiomarker: 'testosterone', adequateThreshold: 25 },
];

// ── All 332 Safety Rules ──
// Merge all 5 rule arrays. CRITICAL rules prevent deaths — they must always be in the hot path.
export const ALL_SAFETY_RULES = [
  ...CRITICAL_SAFETY_RULES,
  ...SEED_SAFETY_RULES,
  ...TIER1_SUPPLEMENT_SAFETY_RULES,
  ...TIER2_RX_SAFETY_RULES,
  ...TIER3_LONGEVITY_SAFETY_RULES,
];

// ── BiomarkerResult -> LabValue Mapping ──

/**
 * Map hemologixStatus to the LabValue status enum.
 *
 * hemologixStatus      -> LabValue status
 * ─────────────────────────────────────────
 * optimal              -> optimal
 * suboptimal           -> below_functional
 * low                  -> below_conventional
 * elevated             -> above_functional
 * critical             -> above_conventional
 */
function mapHemologixStatusToLabStatus(
  hemologixStatus: BiomarkerResult['hemologixStatus'],
  value: number,
  functionalRange: { low: number; high: number },
): LabValue['status'] {
  switch (hemologixStatus) {
    case 'optimal':
      return 'optimal';
    case 'suboptimal':
      // Suboptimal could be above or below functional range
      return value < functionalRange.low ? 'below_functional' : 'above_functional';
    case 'low':
      return 'below_conventional';
    case 'elevated':
      return 'elevated';
    case 'critical':
      // Critical could be above or below — infer from value vs range
      return value < functionalRange.low ? 'below_conventional' : 'above_conventional';
  }
}

/**
 * Convert BiomarkerResult[] (from Lab Analyst) to LabValue[]
 * (consumed by Twin Engine protocol builder).
 *
 * Exported for testing.
 */
export function biomarkersToLabValues(biomarkers: BiomarkerResult[]): LabValue[] {
  return biomarkers.map((bio) => {
    const status = mapHemologixStatusToLabStatus(
      bio.hemologixStatus,
      bio.value,
      bio.functionalRange,
    );

    return {
      biomarker: bio.biomarker,
      value: bio.value,
      unit: bio.unit,
      conventionalRange: `${bio.standardRange.low}-${bio.standardRange.high}`,
      functionalRange: `${bio.functionalRange.low}-${bio.functionalRange.high}`,
      status,
      trigger: bio.hemologixStatus !== 'optimal',
      timestamp: new Date().toISOString(),
    };
  });
}

// ── Real Protocol Pipeline (pre-filter → Phase1 → Phase2 → gate classify) ──

// All formulary products merged — same pattern as provenancePipeline.ts
const ALL_PRODUCTS = [...SEED_SUPPLEMENTS, ...SEED_RX, ...SEED_LONGEVITY];

// ── Box-Scoped Formulary ──
// Each Open Loop box constrains which products the pipeline considers.
// The ad/landing page determines the box BEFORE labs are uploaded.
// Supplements-only boxes → Gate 1 (OTC checkout).
// Rx boxes → Gate 2 (provider review required).

export type PatientBox =
  | 'supplements'   // "I don't feel normal" — supplements only, Gate 1
  | 'hrt'           // Menopause/HRT — estradiol, progesterone, Gate 2
  | 'trt'           // Low T — testosterone cypionate, Gate 2
  | 'ed'            // Erectile dysfunction — sildenafil/tadalafil, Gate 2
  | 'weight_loss'   // MWL — GLP-1 agonists, Gate 2
  | 'longevity'     // Bryan Johnson — peptides + supplements, Gate 2
  | 'nad_peptides';  // NAD/Sermorelin/B12, Gate 2

function getBoxProducts(box: PatientBox): typeof ALL_PRODUCTS {
  switch (box) {
    case 'supplements':
      return SEED_SUPPLEMENTS;
    case 'longevity':
      return [...SEED_SUPPLEMENTS, ...SEED_LONGEVITY];
    case 'hrt':
      return [...SEED_SUPPLEMENTS, ...SEED_RX.filter(p =>
        p.id.includes('estradiol') || p.id.includes('progesterone') ||
        p.id.includes('spironolactone') || p.id === 'rx-testosterone-cream-female' ||
        p.id.includes('dhea'),
      )];
    case 'trt':
      return [...SEED_SUPPLEMENTS, ...SEED_RX.filter(p =>
        p.id.includes('testosterone') || p.id.includes('anastrozole') ||
        p.id.includes('hcg') || p.id.includes('clomiphene') ||
        p.id.includes('enclomiphene') || p.id.includes('dhea'),
      )];
    case 'ed':
      return [...SEED_SUPPLEMENTS, ...SEED_RX.filter(p =>
        p.id.includes('sildenafil') || p.id.includes('tadalafil') ||
        p.id.includes('pt141'),
      )];
    case 'weight_loss':
      return [...SEED_SUPPLEMENTS, ...SEED_RX.filter(p =>
        p.id.includes('semaglutide') || p.id.includes('tirzepatide') ||
        p.id.includes('bupropion') || p.id.includes('metformin'),
      )];
    case 'nad_peptides':
      return [...SEED_SUPPLEMENTS, ...SEED_LONGEVITY.filter(p =>
        p.id.includes('nad') || p.id.includes('sermorelin') ||
        p.id.includes('b12'),
      )];
  }
}

function isBoxGate1(box: PatientBox): boolean {
  return box === 'supplements';
}

/**
 * Run the real deterministic protocol pipeline with safety rules,
 * pre-filtering, and gate classification.
 *
 * Chain: PRO.04 (pre-filter) → Phase1 → Phase2 → PRO.08 (gate classify)
 *
 * Pre-filter runs async (for audit), so this is wrapped in a cached promise.
 * getProtocol() returns the last cached result synchronously; the first call
 * kicks off the async pipeline and falls back to Phase2 only until ready.
 */
function buildPreFilterInput(
  modifiers: ModifierSet,
  sex: 'Male' | 'Female',
  age?: number,
): PreFilterInput {
  return {
    products: ALL_PRODUCTS.map(p => ({
      id: p.id,
      name: p.name,
      gender: p.gender,
      ageMin: (p as unknown as Record<string, unknown>).ageMin as number | undefined,
      ageMax: (p as unknown as Record<string, unknown>).ageMax as number | undefined,
      pregnancyCategory: (p as unknown as Record<string, unknown>).pregnancyCategory as string | undefined,
    })),
    medications: modifiers.medications.map(m =>
      typeof m === 'string' ? m : (m as { name: string }).name,
    ),
    conditions: modifiers.conditions,
    allergies: modifiers.allergies,
    sex: sex === 'Male' ? 'male' : 'female',
    age,
    isPregnant: modifiers.reproductiveStatus === 'pregnant' ? true
      : modifiers.reproductiveStatus === 'not_applicable' ? false
      : undefined,
    dataCompleteness: {
      sexProvided: true,
      ageProvided: age !== undefined,
      pregnancyStatusProvided: modifiers.reproductiveStatus !== undefined,
      allergiesProvided: modifiers.allergies.length > 0,
      medicationsProvided: modifiers.medications.length > 0,
      conditionsProvided: modifiers.conditions.length > 0,
    },
    rulesSnapshotId: 'seed-v1',
  };
}

/**
 * Run gate classification on the Phase2 protocol output.
 * Returns the gate level (1 or 2) based on 7 criteria.
 */
function classifyProtocolGate(
  protocol: ReturnType<typeof runPhase2>,
  modifiers: ModifierSet,
  sex: 'Male' | 'Female',
  biomarkers: BiomarkerResult[],
  preFilterExclusions: Array<{ ruleId?: string; productId?: string; productName: string; reason: string }>,
  patientAge?: number,
): { gate: 'GATE_1' | 'GATE_2'; reasoning: string } {
  // Check if any recommended products require Rx
  const hasRx = protocol.protocol.some(p => {
    const prod = ALL_PRODUCTS.find(ap => ap.id === p.productId);
    return prod?.requiresRx === true;
  });

  // If any product requires Rx, it's Gate 2
  if (hasRx) {
    return { gate: 'GATE_2', reasoning: 'Protocol contains Rx products requiring provider review' };
  }

  // Build biomarker results for deviation check
  const biomarkerResults = biomarkers.map(b => ({
    biomarker: b.biomarker,
    value: b.value,
    unit: b.unit,
    standardRange: { low: b.standardRange.low, high: b.standardRange.high },
    functionalRange: { low: b.functionalRange.low, high: b.functionalRange.high },
    eliteAverage: b.eliteAverage,
    standardStatus: b.standardStatus,
    hemologixStatus: b.hemologixStatus,
    category: b.category,
    interpretation: b.interpretation,
  }));

  const productDetails = protocol.protocol.map(p => {
    const prod = ALL_PRODUCTS.find(ap => ap.id === p.productId);
    return {
      productId: p.productId,
      name: p.productName,
      tier: prod?.tier ?? 1,
      requiresRx: prod?.requiresRx ?? false,
    };
  });

  try {
    const gateResult = classifyGate({
      protocol: {
        id: `live-${Date.now()}`,
        createdAt: new Date().toISOString(),
        supplements: protocol.protocol.map(p => {
          const prod = ALL_PRODUCTS.find(ap => ap.id === p.productId);
          return {
            name: p.productName,
            dosage: p.dosage,
            timing: p.timing.toUpperCase() as 'AM_ENERGY' | 'MIDDAY_ABSORPTION' | 'PM_RECOVERY',
            rationale: p.reasoning,
            targetBiomarker: p.triggerBiomarker,
            gender: (prod?.gender ?? 'both') as 'M' | 'F' | 'both',
            contraindications: prod?.contraindications ?? [],
            interactionWarnings: (prod as unknown as Record<string, unknown>)?.interactionWarnings as string[] ?? [],
            evidenceLevel: prod?.evidenceLevel ?? 'moderate',
            productId: p.productId,
            requiresRx: prod?.requiresRx ?? false,
          };
        }),
        summary: `${protocol.protocol.length} targeted supplements`,
        tierRecommendation: 1,
        warnings: protocol.warnings.map(w => w.message),
      },
      patientProfile: {
        sex: sex === 'Male' ? 'male' : 'female',
        age: patientAge,
        isPregnant: modifiers.reproductiveStatus === 'pregnant' ? true : undefined,
        isBreastfeeding: modifiers.reproductiveStatus === 'breastfeeding' ? true : undefined,
        medications: modifiers.medications.map(m =>
          typeof m === 'string' ? m : (m as { name: string }).name,
        ),
        allergies: modifiers.allergies,
        conditions: modifiers.conditions,
      },
      biomarkerResults,
      safetyViolations: [],
      preFilterExclusions: preFilterExclusions.map(ex => ({
        ruleId: ex.ruleId ?? 'PRO.04',
        productId: ex.productId ?? '',
        productName: ex.productName,
        severity: 'block' as const,
        reason: ex.reason,
      })),
      postValidationWarnings: protocol.warnings.map(w => w.message),
      conservativeDefaultsApplied: false,
      conservativeDefaultFields: [],
      productDetails,
    });
    return { gate: gateResult.gate, reasoning: gateResult.reasoning };
  } catch (err) {
    console.error('[LiveDataSource] Gate classification failed, defaulting to Gate 2:', err);
    return { gate: 'GATE_2', reasoning: 'Gate classification error — defaulting to provider review' };
  }
}

// ── Factory ──

/**
 * Create a live ChatDataSource wired to real Gemini, Lab Analyst,
 * and the real protocol pipeline (pre-filter → Phase1 → Phase2 → gate).
 *
 * Async because it loads the brand display context from Firestore
 * and pre-runs the safety pre-filter.
 */
export async function createLiveDataSource(
  brandId: string,
  sex: 'Male' | 'Female',
  box: PatientBox = 'supplements',
  age?: number,
  orchestrator?: NurseOrchestrator,
): Promise<ChatDataSource> {
  const brandDisplay = await loadBrandDisplay(brandId);
  const collector = new ModifierCollector();

  // Cache for pre-filter results (re-run on modifier changes)
  let cachedPreFilterBlockedIds: Set<string> = new Set();
  let cachedPreFilterExclusions: Array<{ ruleId?: string; productId?: string; productName: string; reason: string }> = [];
  let lastModifierHash = '';
  // A2: Track which medications/conditions the pre-filter has seen
  let preFilterSeenMeds: Set<string> = new Set();
  let preFilterSeenConditions: Set<string> = new Set();

  async function updatePreFilter(modifiers: ModifierSet) {
    const hash = JSON.stringify({
      meds: modifiers.medications,
      conds: modifiers.conditions,
      allergies: modifiers.allergies,
      repro: modifiers.reproductiveStatus,
    });
    if (hash === lastModifierHash) return;
    lastModifierHash = hash;

    try {
      const input = buildPreFilterInput(modifiers, sex, age);
      // Scope pre-filter to box products only — no point filtering products not in the box
      const boxProducts = getBoxProducts(box);
      input.products = boxProducts.map(p => ({
        id: p.id,
        name: p.name,
        gender: p.gender,
        ageMin: (p as unknown as Record<string, unknown>).ageMin as number | undefined,
        ageMax: (p as unknown as Record<string, unknown>).ageMax as number | undefined,
        pregnancyCategory: (p as unknown as Record<string, unknown>).pregnancyCategory as string | undefined,
      }));
      const result = await runPreFilter(input, ALL_SAFETY_RULES);
      cachedPreFilterBlockedIds = new Set(result.exclusions.map(ex => ex.productId));
      cachedPreFilterExclusions = result.exclusions.map(ex => ({
        ruleId: ex.ruleId,
        productId: ex.productId,
        productName: ex.productName,
        reason: ex.reason,
      }));
      // A2: Track what the pre-filter has seen
      for (const m of modifiers.medications) {
        preFilterSeenMeds.add(typeof m === 'string' ? m : (m as { name: string }).name.toLowerCase());
      }
      for (const c of modifiers.conditions) {
        preFilterSeenConditions.add(c.toLowerCase());
      }
    } catch (err) {
      console.error('[LiveDataSource] Pre-filter failed:', err);
    }
  }

  // Run initial pre-filter with empty modifiers
  const { EMPTY_MODIFIER_SET } = await import('../../types/patientJourney');
  await updatePreFilter(EMPTY_MODIFIER_SET);

  return {
    mode: 'live',
    brandDisplay,

    extractSymptoms: (msg, turn, existing) =>
      extractSignals(msg, turn, existing),

    analyzeLabs: (file, signals) =>
      streamLabAnalysis(file, false, signals),

    getProtocol: (biomarkers, modifiers) => {
      // When orchestrator is present, merge real NRS.08 safety data into modifiers.
      // This replaces EMPTY_MODIFIER_SET with real patient data from modals + chat.
      if (orchestrator) {
        const nurseModifiers = orchestrator.getModifiers();
        // Merge: nurse data fills gaps, caller data (from late reveal) takes precedence
        if (modifiers.medications.length === 0) modifiers.medications = nurseModifiers.medications;
        if (modifiers.allergies.length === 0) modifiers.allergies = nurseModifiers.allergies;
        if (modifiers.conditions.length === 0) modifiers.conditions = nurseModifiers.conditions;
        if (modifiers.reproductiveStatus === 'not_applicable' && nurseModifiers.reproductiveStatus !== 'not_applicable') {
          modifiers.reproductiveStatus = nurseModifiers.reproductiveStatus;
        }
        if (modifiers.contraindications.length === 0) modifiers.contraindications = nurseModifiers.contraindications;
        if (modifiers.goals.length === 0) modifiers.goals = nurseModifiers.goals;
      }

      // ── A2: Pre-filter update on modifier change ──
      // Re-run pre-filter if modifiers changed (async, cached).
      // Safety net: HRT defaults also check product-level contraindications (E1),
      // and all HRT routes through Gate 2 (provider review before Rx ships).
      updatePreFilter(modifiers).catch(err =>
        console.error('[LiveDataSource] Background pre-filter update failed:', err),
      );

      // ── Box-as-goal defaults ──
      // When patient hasn't set explicit goals, derive from context.
      // Goals flow into Phase2 via modifiers.
      if (modifiers.goals.length === 0) {
        const rs = modifiers.reproductiveStatus;
        if (rs === 'postmenopausal' || rs === 'trying_to_conceive' || rs === 'pregnant' || rs === 'breastfeeding') {
          modifiers.goals = ['hormonal'];
        } else {
          modifiers.goals = ['energy', 'immune'];
        }
      }

      const labValues = biomarkersToLabValues(biomarkers);
      const phase1 = runPhase1(labValues);
      const demographics: Demographics = {
        name: '',
        age: 50, // default when unknown
        sex,
        state: '',
      };

      // ── BOX-SCOPED FORMULARY ──
      // The patient's box (set by ad/landing page) constrains which products
      // enter Phase2. A "supplements" box patient never sees Rx products.
      // A "longevity" box patient sees peptides. The box determines the gate.
      const boxProductIds = new Set(getBoxProducts(box).map(p => p.id));

      const phase2 = runPhase2(phase1, modifiers, demographics);

      // Track products blocked by contraindication checks (E1, A5)
      const blockedByContraindication: Array<{ productName: string; reason: string }> = [];

      // Filter to box-scoped + pre-filter-safe products
      let boxProtocol = phase2.protocol.filter(p => {
        if (!boxProductIds.has(p.productId)) return false;
        if (cachedPreFilterBlockedIds.has(p.productId)) return false;
        return true;
      });

      // ── HRT Box Clinical Defaults ──
      // When a patient enters the HRT box, they came for hormone therapy.
      // If key hormone labs (estradiol, progesterone) are absent from the panel,
      // Phase2 can't trigger HRT products. But a postmenopausal woman doesn't need
      // an estradiol lab to prove she needs estradiol — FSH > 25 or reproductive
      // status 'postmenopausal' is sufficient clinical context.
      // Inject missing core HRT products as "recommended pending labs" — they route
      // through Gate 2 (provider confirms before patient receives anything).
      if (box === 'hrt' && sex === 'Female') {
        const isPostmenopausal = modifiers.reproductiveStatus === 'postmenopausal'
          || biomarkers.some(b => {
            const name = b.biomarker.toLowerCase();
            return (name.includes('fsh') || name.includes('follicle')) && b.value > 25;
          });
        // A7: Improved surgical menopause condition matching
        const isSurgicalMenopause = modifiers.conditions.some(c => {
          const cl = c.toLowerCase();
          return cl.includes('oophorectomy') || cl.includes('surgical menopause') ||
            cl.includes('bso') || cl.includes('ovaries removed') ||
            cl.includes('ovariectomy') || cl.includes('tah-bso') ||
            cl.includes('bilateral salpingo');
        });

        if (isPostmenopausal || isSurgicalMenopause) {
          // A3: Age guard — premature ovarian insufficiency for age < 35
          const patientAge = age;
          const isPOI = patientAge !== undefined && patientAge < 35;

          const protocolIds = new Set(boxProtocol.map(p => p.productId));
          const boxProducts = getBoxProducts(box);

          for (const hrt of CORE_HRT_DEFAULTS) {
            // Skip if already in protocol, blocked by safety, or not in box
            if (protocolIds.has(hrt.id)) continue;
            if (cachedPreFilterBlockedIds.has(hrt.id)) continue;
            if (!boxProductIds.has(hrt.id)) continue;
            const product = boxProducts.find(p => p.id === hrt.id);
            if (!product) continue;

            // A4: Check existing adequate biomarker levels before injecting
            // L2-06: Match common biomarker aliases (e2, p4, total testosterone, etc.)
            const estradiolAliases = ['estradiol', 'e2', 'oestradiol', '17-beta estradiol'];
            const progesteroneAliases = ['progesterone', 'p4'];
            const testosteroneAliases = ['testosterone', 'total testosterone'];
            if (hrt.checkBiomarker && hrt.adequateThreshold !== undefined) {
              const aliases = hrt.checkBiomarker === 'estradiol' ? estradiolAliases
                : hrt.checkBiomarker === 'progesterone' ? progesteroneAliases
                : testosteroneAliases;
              const existingBio = biomarkers.find(b => {
                const name = b.biomarker.toLowerCase();
                return aliases.some(a => name.includes(a));
              });
              if (existingBio) {
                // For testosterone in females, also check status isn't low/critical
                if (hrt.checkBiomarker === 'testosterone') {
                  if (existingBio.value >= hrt.adequateThreshold &&
                      existingBio.hemologixStatus !== 'low' && existingBio.hemologixStatus !== 'critical') {
                    continue; // Adequate — skip injection
                  }
                } else {
                  if (existingBio.value >= hrt.adequateThreshold) {
                    continue; // Adequate — skip injection
                  }
                }
              }
            }

            // E1: Check product-level contraindications against patient conditions
            const productContraindications = product.contraindications ?? [];
            let matchedCi = '';
            let matchedCond = '';
            const conditionBlocked = productContraindications.some(ci => {
              const ciLower = ci.toLowerCase();
              return modifiers.conditions.some(cond => {
                const condLower = cond.toLowerCase();
                let hit = false;
                if (ciLower.includes('cancer') && condLower.includes('cancer')) hit = true;
                if (ciLower.includes('hormone-sensitive') && condLower.includes('cancer')) hit = true;
                if (ciLower.includes('thrombo') && condLower.includes('thrombo')) hit = true;
                if (ciLower.includes('dvt') && condLower.includes('dvt')) hit = true;
                if (ciLower.includes('dvt') && (condLower.includes('dvt') || condLower.includes('deep vein'))) hit = true;
                if ((ciLower.includes('thrombo') || ciLower.includes('dvt')) && condLower.includes('pulmonary embolism')) hit = true;
                if ((ciLower.includes('thrombo') || ciLower.includes('dvt')) && condLower.includes('blood clot')) hit = true;
                if ((ciLower.includes('thrombo') || ciLower.includes('dvt')) && /\bpe\b/.test(condLower)) hit = true;
                if (ciLower.includes('liver disease') && condLower.includes('liver')) hit = true;
                if (ciLower.includes('hepatic') && condLower.includes('liver')) hit = true;
                if (ciLower.includes('liver') && condLower.includes('hepatic')) hit = true;
                if (ciLower.includes('hepatic') && condLower.includes('hepatic')) hit = true;
                if (hit) { matchedCi = ci; matchedCond = cond; }
                return hit;
              });
            });
            // Separate allergen check — peanut allergy for Prometrium (L2-02: must be outside condition loop)
            const allergenBlocked = productContraindications.some(ci => {
              const ciLower = ci.toLowerCase();
              if (ciLower.includes('peanut')) {
                const hit = modifiers.allergies.some(a => a.toLowerCase().includes('peanut'));
                if (hit) { matchedCi = ci; matchedCond = 'allergy: peanut'; }
                return hit;
              }
              return false;
            }) || ((product as any).allergenFlags ?? []).some((flag: string) => {
              const flagLower = flag.toLowerCase();
              const hit = modifiers.allergies.some(a => a.toLowerCase().includes(flagLower.split(' ')[0]));
              if (hit) { matchedCi = flag; matchedCond = `allergy: ${flagLower}`; }
              return hit;
            });
            if (conditionBlocked || allergenBlocked) {
              // L2-03: Include specific contraindication and condition in blocked reason
              blockedByContraindication.push({
                productName: product.name,
                reason: `Contraindication: "${matchedCi}" — patient has "${matchedCond}"`,
              });
              continue;
            }

            boxProtocol.push({
              productId: product.id,
              productName: product.name,
              dosage: product.dosage,
              timing: product.timing.toLowerCase() as 'am_energy' | 'midday_absorption' | 'pm_recovery',
              reasoning: isPOI ? `${hrt.reason} (POI — specialist dosing required)` : hrt.reason,
              triggerBiomarker: isPOI ? 'clinical_default_hrt_poi' : 'clinical_default_hrt',
              tier: (product.tier ?? 2) as 1 | 2 | 3,
            });
          }

          // A4 (post): Remove HRT products from protocol if biomarkers show adequate levels
          // This catches products added by Phase2 (via targetBiomarker matching) AND by defaults
          const estradiolAliasesPost = ['estradiol', 'e2', 'oestradiol', '17-beta estradiol'];
          const progesteroneAliasesPost = ['progesterone', 'p4'];
          const testosteroneAliasesPost = ['testosterone', 'total testosterone'];
          for (const hrt of CORE_HRT_DEFAULTS) {
            if (!hrt.checkBiomarker || hrt.adequateThreshold === undefined) continue;
            if (!boxProtocol.some(p => p.productId === hrt.id)) continue;
            const aliases = hrt.checkBiomarker === 'estradiol' ? estradiolAliasesPost
              : hrt.checkBiomarker === 'progesterone' ? progesteroneAliasesPost
              : testosteroneAliasesPost;
            const existingBio = biomarkers.find(b => aliases.some(a => b.biomarker.toLowerCase().includes(a)));
            if (!existingBio) continue;
            const isAdequate = hrt.checkBiomarker === 'testosterone'
              ? (existingBio.value >= hrt.adequateThreshold && existingBio.hemologixStatus !== 'low' && existingBio.hemologixStatus !== 'critical')
              : existingBio.value >= hrt.adequateThreshold;
            if (isAdequate) {
              boxProtocol = boxProtocol.filter(p => p.productId !== hrt.id);
              blockedByContraindication.push({
                productName: ALL_PRODUCTS.find(p => p.id === hrt.id)?.name ?? hrt.id,
                reason: `${hrt.checkBiomarker} at ${existingBio.value} ${existingBio.unit} — adequate, no supplementation needed`,
              });
            }
          }

          // A5: Unopposed estrogen safety check
          // If estradiol is in protocol but progesterone is NOT, and patient has not
          // had a hysterectomy, remove estradiol (unopposed estrogen → endometrial cancer risk)
          const hasEstradiol = boxProtocol.some(p => p.productId === 'rx-estradiol-patch');
          const hasProgesterone = boxProtocol.some(p => p.productId === 'rx-progesterone-prometrium');
          // L2-07: Match TAH-BSO and other hysterectomy variants
          const hasHysterectomy = modifiers.conditions.some(c => {
            const cl = c.toLowerCase();
            return cl.includes('hysterectomy') || cl.includes('tah-bso') || cl.includes('tah ') ||
              cl.includes('uterus removed') || cl.includes('uterine removal');
          });
          if (hasEstradiol && !hasProgesterone && !hasHysterectomy) {
            boxProtocol = boxProtocol.filter(p => p.productId !== 'rx-estradiol-patch');
            blockedByContraindication.push({
              productName: 'Estradiol Patch',
              reason: 'Unopposed estrogen risk — progesterone required for endometrial protection',
            });
          }
        }
      }

      // Track what got blocked and why
      const blockedBySafety = phase2.protocol
        .filter(p => cachedPreFilterBlockedIds.has(p.productId))
        .map(p => {
          const exclusion = cachedPreFilterExclusions.find(ex => ex.productId === p.productId);
          return { productName: p.productName, reason: exclusion?.reason ?? 'Blocked by safety rules' };
        });
      const outOfBox = phase2.protocol
        .filter(p => !boxProductIds.has(p.productId) && !cachedPreFilterBlockedIds.has(p.productId))
        .map(p => ({
          productName: p.productName,
          reason: `Outside ${box} box — available in a different program`,
        }));

      // Gate: supplements box → Gate 1 (no Rx). All other boxes → classify.
      let gateLevel: 1 | 2;
      if (isBoxGate1(box)) {
        // Supplements-only box: pass REAL biomarkers and REAL exclusions to gate classifier.
        // Post-classify: escalate only genuinely dangerous triggers to Gate 2.
        // Suppress triggers that are already handled by pre-filter or are mild.
        if (boxProtocol.length > 0) {
          const { gate } = classifyProtocolGate(
            { ...phase2, protocol: boxProtocol },
            modifiers, sex,
            biomarkers,                // Real biomarkers — catches severe deviations
            cachedPreFilterExclusions,  // Real exclusions — full safety picture
          );

          if (gate === 'GATE_2') {
            // Re-classify: check if any ESCALATING triggers fired
            // Escalate: HIGH_RISK_MEDICATION, PREGNANCY, BREASTFEEDING,
            //           BIOMARKER_DEVIATION where SD > 3.0 (severe)
            // Suppress: CONTRAINDICATION_RULE (product already blocked by pre-filter),
            //           BIOMARKER_DEVIATION where SD ≤ 3.0 (mild — supplements appropriate),
            //           PRODUCT_TIER, CONSERVATIVE_DEFAULT
            // Check for severe biomarker deviations (SD > 3.0 from optimal midpoint)
            // Uses the same non-normal biomarker exception as the gate classifier:
            // TSH, Ferritin, Vitamin D use range-based fallback (value < low*0.5 or > high*2.0)
            const NON_NORMAL = new Set(['ferritin', 'tsh', 'thyroid stimulating hormone', 'vitamin d', '25-hydroxyvitamin d']);
            const hasSevereBiomarkerDeviation = biomarkers.some(b => {
              const range = b.functionalRange;
              if (!range || range.low >= range.high) return false;
              const name = b.biomarker.toLowerCase().trim();
              if (NON_NORMAL.has(name)) {
                // Range-based: severe only if way outside optimal
                return b.value < range.low * 0.5 || b.value > range.high * 2.0;
              }
              const midpoint = (range.low + range.high) / 2;
              const halfRange = (range.high - range.low) / 2;
              const sd = Math.abs((b.value - midpoint) / halfRange);
              return sd > 3.0;
            });
            // Check for high-risk medication interactions
            const meds = modifiers.medications.map(m =>
              typeof m === 'string' ? m : (m as { name: string }).name,
            ).map(m => m.toLowerCase());
            const hasHighRiskMed = meds.some(m =>
              ['warfarin', 'coumadin', 'heparin', 'enoxaparin', 'rivaroxaban',
               'apixaban', 'dabigatran', 'cyclosporine', 'tacrolimus',
               'digoxin', 'amiodarone', 'lithium', 'phenytoin', 'carbamazepine',
               'methotrexate', 'insulin'].some(hr => m.includes(hr)),
            );
            const isPregnant = modifiers.reproductiveStatus === 'pregnant';
            const isBreastfeeding = modifiers.reproductiveStatus === 'breastfeeding';

            if (hasHighRiskMed || isPregnant || isBreastfeeding || hasSevereBiomarkerDeviation) {
              gateLevel = 2;
            } else {
              // All triggers are mild/already-handled — stay Gate 1
              gateLevel = 1;
            }
          } else {
            gateLevel = 1;
          }
        } else {
          gateLevel = 1;
        }
      } else {
        // Rx/longevity boxes always start at Gate 2 — provider in the loop
        gateLevel = 2;
      }

      // ── Provider Referral (first line) ──
      // When the patient needs a board-certified physician, say so FIRST.
      // Two scenarios: (1) Gate 2 products in protocol → connect with provider for Rx
      //                (2) Safety blocks removed products → oncology/specialist clearance needed
      let providerReferral: { needed: boolean; reason: string; blockedForSafety: string[] } | null = null;

      const rxInProtocol = boxProtocol.filter(p => {
        const prod = ALL_PRODUCTS.find(ap => ap.id === p.productId);
        return prod?.requiresRx;
      });
      const safetyBlockedNames = blockedByContraindication.map(b => b.productName);

      if (safetyBlockedNames.length > 0 && gateLevel === 2) {
        // Cancer/DVT/safety block — provider + specialist clearance needed
        providerReferral = {
          needed: true,
          reason: `A board-certified physician will review your protocol. Some products were blocked for safety — your provider can discuss alternatives or request specialist clearance.`,
          blockedForSafety: safetyBlockedNames,
        };
      } else if (rxInProtocol.length > 0) {
        // Gate 2 Rx products — provider prescribes
        providerReferral = {
          needed: true,
          reason: `A board-certified physician will review and prescribe your protocol. Your supplements ship today — your Rx ships after provider approval.`,
          blockedForSafety: [],
        };
      } else if (gateLevel === 2) {
        // Gate 2 for other reasons (high-risk meds, severe biomarkers)
        providerReferral = {
          needed: true,
          reason: `A board-certified physician will review your protocol before it ships.`,
          blockedForSafety: [],
        };
      }

      return {
        ...phase2,
        protocol: boxProtocol,
        blockedProducts: [...phase2.blockedProducts, ...blockedBySafety, ...outOfBox, ...blockedByContraindication],
        gateLevel,
        providerReferral,
      };
    },

    nurseRespond: orchestrator
      ? (history, systemPrompt, userMsg) => orchestrator.nurseRespond(history, systemPrompt, userMsg)
      : (history, systemPrompt, userMsg) => geminiChat('gemini-2.5-flash', systemPrompt, history, userMsg, 0.7, 512),

    detectLateReveal: (msg, _turn) => {
      // When orchestrator is present, also scan for disclosures via NRS.06
      if (orchestrator) {
        orchestrator.scanForDisclosures(msg, _turn);
      }

      const detected = collector.detectLateReveal(msg);
      if (!detected) return null;

      const needsRerun = collector.shouldRerunPhase2(detected);

      // Build human-readable change list
      const safetyChanges: string[] = [];
      if (detected.medications) {
        for (const med of detected.medications) {
          safetyChanges.push(`${med.name} (${med.category})`);
        }
      }
      if (detected.conditions) {
        for (const cond of detected.conditions) {
          safetyChanges.push(cond);
        }
      }
      if (detected.allergies) {
        for (const allergy of detected.allergies) {
          safetyChanges.push(`allergy: ${allergy}`);
        }
      }
      if (detected.reproductiveStatus) {
        safetyChanges.push(detected.reproductiveStatus);
      }

      return {
        needsRerun,
        safetyChanges,
        updatedModifiers: collector.getModifiers(),
      };
    },

    getNurseReinforcement: (signals, turn) =>
      buildCaptureReinforcement(signals, DEFAULT_CAPTURE_CONFIG, turn),
  };
}
